import { useCallback, useMemo, useRef } from 'react'
import { useAuth as useOidcAuth } from 'react-oidc-context'

/** Kept for call-site compatibility with the old @auth0/auth0-react signature. */
export type GetTokenSilentlyOptions = Record<string, unknown>

export interface AuthUser {
  sub?: string
  name?: string
  given_name?: string
  family_name?: string
  email?: string
  phone_number?: string
  picture?: string
}

/** Mirrors the old @auth0/auth0-react loginWithRedirect options. */
export interface LoginWithRedirectOptions {
  appState?: { returnTo?: string }
}

export interface Auth {
  isAuthenticated: boolean
  isLoading: boolean
  error?: Error
  user?: AuthUser
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>
  loginWithRedirect: (options?: LoginWithRedirectOptions) => Promise<void>
  logout: (options?: { logoutParams?: { returnTo?: string } }) => Promise<void>
}

/**
 * Drop-in replacement for useAuth0(), backed by react-oidc-context.
 * Works against any OIDC provider (Keycloak, Auth0, ...).
 *
 * CRITICAL: the returned functions MUST keep a stable identity across renders.
 * Callers put them in useEffect dependency arrays (e.g. AppContent's
 * "not authenticated -> loginWithRedirect" effect). react-oidc-context returns
 * a NEW `auth` object every render, so deriving the callbacks from `auth`
 * directly (even via useCallback([auth])) yields a fresh identity each render,
 * which re-runs those effects every render. Combined with signinRedirect being
 * rejected with "already in progress" while one is in flight, that becomes a
 * synchronous render loop that pins the main thread — the page shows
 * "This page isn't responding" and never finishes redirecting. (Fast machines
 * sometimes win the navigation race and hide it; slower machines hang.)
 *
 * Fix: hold the latest `auth` in a ref and expose callbacks with EMPTY deps, so
 * their identity is fixed for the component's lifetime while still reading the
 * current auth. isLoading also stays true while a redirect is active
 * (activeNavigator) so the caller's guard fires exactly once.
 */
export const useAuth = (): Auth => {
  const auth = useOidcAuth()

  const authRef = useRef(auth)
  authRef.current = auth

  const getAccessTokenSilently = useCallback(async () => {
    const current = authRef.current
    if (current.user && !current.user.expired) {
      return current.user.access_token
    }
    const user = await current.signinSilent()
    if (!user) throw new Error('Silent sign-in failed - the session has expired')
    return user.access_token
  }, [])

  const loginWithRedirect = useCallback(async (options?: LoginWithRedirectOptions) => {
    const returnTo = options?.appState?.returnTo ?? window.location.pathname
    await authRef.current.signinRedirect({ state: { returnTo } })
  }, [])

  const logout = useCallback(async (options?: { logoutParams?: { returnTo?: string } }) => {
    await authRef.current.signoutRedirect({
      post_logout_redirect_uri: options?.logoutParams?.returnTo ?? window.location.origin,
    })
  }, [])

  return useMemo(
    () => ({
      isAuthenticated: auth.isAuthenticated,
      // Loading only matters BEFORE authentication (initial load + callback
      // processing). Once authenticated, a background/on-demand signinSilent
      // sets react-oidc-context's isLoading true again — gating the app on that
      // makes AppContent swap Loader<->AppAuthWrapper, remounting the whole tree
      // in a loop (each remount re-fires useAsyncFetch + FeatureFlagProvider =
      // the GetCurrentUserMetadata + sdkConfig request storm). So: pre-auth only.
      isLoading: auth.isLoading && !auth.isAuthenticated,
      error: auth.error,
      user: auth.user?.profile,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
    }),
    [
      auth.isAuthenticated,
      auth.isLoading,
      auth.error,
      auth.user,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
    ],
  )
}
